import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, ArrowUpDown, Download } from "lucide-react";
import { api, errMsg, fmtDay, downloadBlob } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { PageHeader } from "@/components/StatusBadge";

const Stat = ({ label, value, testid }) => (
  <div className="rounded-lg border border-slate-200 bg-white shadow-sm p-4">
    <div className="text-[11px] uppercase tracking-wider font-semibold text-slate-500">{label}</div>
    <div className="text-2xl font-bold text-slate-900 mt-1" data-testid={testid}>{value}</div>
  </div>
);

const Pct = ({ v }) => v == null ? <span className="text-slate-400">—</span> : (
  <div className="flex items-center gap-2 min-w-[120px]">
    <div className="h-2 flex-1 rounded-full bg-slate-200 overflow-hidden">
      <div className={`h-full ${v >= 75 ? "bg-emerald-500" : v >= 50 ? "bg-amber-500" : "bg-rose-500"}`} style={{ width: `${v}%` }} />
    </div>
    <span className="text-xs font-bold text-slate-800 w-9 text-right">{v}%</span>
  </div>
);

const Mark = ({ s }) => s === "present" ? <span className="text-emerald-700 font-bold">P</span>
  : s === "absent" ? <span className="text-rose-600 font-bold">A</span> : <span className="text-slate-300">·</span>;

export default function AttendanceHistory() {
  const [d, setD] = useState(null);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState({ key: "name", dir: 1 });
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/attendance/history").then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e))); }, []);
  const rows = useMemo(() => {
    if (!d) return [];
    const s = q.trim().toLowerCase();
    const val = (m) => sort.key === "name" ? m.name.toLowerCase() : (m[sort.key] ?? -1);
    return d.members.filter((m) => !s || m.name.toLowerCase().includes(s))
      .sort((a, b) => (val(a) > val(b) ? 1 : val(a) < val(b) ? -1 : 0) * sort.dir);
  }, [d, q, sort]);
  if (!d) return <div className="p-10 grid place-items-center"><Loader2 className="w-5 h-5 animate-spin text-slate-400" /></div>;
  const head = (key, label) => (
    <button className="inline-flex items-center gap-1 hover:text-slate-900" onClick={() => setSort({ key, dir: sort.key === key ? -sort.dir : key === "name" ? 1 : -1 })} data-testid={`history-sort-${key}`}>
      {label}<ArrowUpDown className={`w-3 h-3 ${sort.key === key ? "text-amber-600" : "opacity-40"}`} />
    </button>
  );
  const exp = async (fmt) => {
    setBusy(fmt);
    try { await downloadBlob(`/attendance/history/export?format=${fmt}`, `MIT_Attendance.${fmt}`); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(""); }
  };
  return (
    <div>
      <PageHeader eyebrow="MIT Minutes" title="Attendance History" testid="history-title">
        <Button variant="outline" disabled={!!busy || !d.meetings.length} onClick={() => exp("xlsx")} data-testid="history-export-xlsx">
          {busy === "xlsx" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}Excel</Button>
        <Button variant="outline" disabled={!!busy || !d.meetings.length} onClick={() => exp("pdf")} data-testid="history-export-pdf">
          {busy === "pdf" ? <Loader2 className="w-4 h-4 mr-1 animate-spin" /> : <Download className="w-4 h-4 mr-1" />}PDF</Button>
      </PageHeader>
      <div className="grid grid-cols-2 lg:grid-cols-3 gap-3 mb-5">
        <Stat label="MITs with attendance" value={d.meetings.length} testid="history-meetings-count" />
        <Stat label="Members" value={d.members.length} testid="history-members-count" />
        <Stat label="Average present" value={d.average_percent == null ? "—" : `${d.average_percent}%`} testid="history-average" />
      </div>
      {d.meetings.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white p-10 text-center text-slate-600" data-testid="history-empty">
          No attendance taken yet. Mark attendance inside <Link to="/minutes" className="underline text-amber-700">MIT Minutes</Link> and save.
        </div>
      ) : (
        <div className="rounded-lg border border-slate-200 bg-white shadow-sm">
          <div className="p-3 border-b border-slate-100 flex flex-wrap items-center gap-3">
            <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Find a member…" className="max-w-sm bg-white" data-testid="history-search" />
            <span className="text-xs text-slate-500">P = present · A = absent · percentage counts only meetings where the member was marked</span>
          </div>
          <div className="max-h-[65vh] overflow-auto">
            <table className="text-sm w-full" data-testid="history-table">
              <thead className="sticky top-0 z-10 bg-slate-100 text-slate-600 text-xs">
                <tr>
                  <th className="p-2 text-left sticky left-0 bg-slate-100 z-10">{head("name", "Member")}</th>
                  <th className="p-2 text-left">{head("percent", "% present")}</th>
                  <th className="p-2 text-center">{head("present", "Present")}</th>
                  <th className="p-2 text-center">{head("absent", "Absent")}</th>
                  <th className="p-2 text-center">Not marked</th>
                  {d.meetings.map((m) => (
                    <th key={m.minutes_id} className="p-2 text-center whitespace-nowrap font-medium">
                      <Link to={`/minutes/${m.minutes_id}`} className="hover:underline" title={m.fields?.venue} data-testid={`history-meeting-${m.minutes_id}`}>
                        {m.fields?.date ? fmtDay(m.fields.date) : m.number}
                      </Link>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {rows.map((m) => (
                  <tr key={m.row_id} className="border-t border-slate-100 hover:bg-slate-50" data-testid={`history-row-${m.row_id}`}>
                    <td className="p-2 font-medium text-slate-900 sticky left-0 bg-white whitespace-nowrap">{m.name}</td>
                    <td className="p-2" data-testid={`history-percent-${m.row_id}`}><Pct v={m.percent} /></td>
                    <td className="p-2 text-center text-emerald-700 font-semibold">{m.present}</td>
                    <td className="p-2 text-center text-rose-600 font-semibold">{m.absent}</td>
                    <td className="p-2 text-center text-slate-500">{m.unmarked}</td>
                    {d.meetings.map((x) => <td key={x.minutes_id} className="p-2 text-center"><Mark s={m.marks[x.minutes_id]} /></td>)}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}
